import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CompanyHomeService, MasterService } from '../api/services.gen';
import { handleErrorResponse } from "../utils/util";
import { USER_ROLE, TALENT_TYPE_OPTION_TYPE } from "../constants/index";
import {
  TalentJobChange, 
  CompanyDashboardMemberData, 
  CompanyHomeTalentChartRetrieveData, 
  CompanyHomeTalentHasChangedJobRetrieveData,
  CompanyTopNews,
  CompanyHomeTopViewNewsAndJobRetrieveData,
  CompanyDashboardChartData,
  TopJob,
  MasterOptionsMasterListData
} from 'js/api/types.gen';
import i18n from 'i18next';
import { resetAppState } from "../store/common";

export interface SelectedTime {
  name: string;
  id: number;
}

interface CompanyHomeState {
  rankAOnly: boolean;
  chartSelectedTime: SelectedTime;
  optionTimes: SelectedTime[];
  isLoadingChart: boolean;
  memberRegisteredInfo: CompanyDashboardMemberData[];
  isLoadingTalentHasChangedJob: boolean;
  talentHasChangedJob: TalentJobChange[];
  talentNewRegistrations: TalentJobChange[];
  isLoadingTopNews: boolean;
  topNews: CompanyTopNews[];
  topJobs: TopJob[];
  chartData: CompanyDashboardChartData;
  isOpenDetailTalentPanel: boolean;
  typeOfTalent?: any;
  selectedTypeOfTalent?: any;
}
const initialState: CompanyHomeState = {
  rankAOnly: false,
  chartSelectedTime: { name: '過去30日間', id: 3 },
  optionTimes: [
    { name: '今月', id: 1 },
    { name: '先月', id: 2 },
    { name: '過去30日間', id: 3 },
    { name: '過去90日間', id: 4 },
    { name: '過去1年間', id: 5 },
    { name: '全期間（週）', id: 6 },
    { name: '全期間（月）', id: 7 },
  ],
  isLoadingChart: true,
  memberRegisteredInfo: [],
  isLoadingTalentHasChangedJob: true,
  talentHasChangedJob: [],
  talentNewRegistrations: [],
  isLoadingTopNews: true,
  topNews: [],
  topJobs: [],
  chartData:{} as CompanyDashboardChartData,
  isOpenDetailTalentPanel: false,
  typeOfTalent: [{name: 'すべて表示', id: 0}],
  selectedTypeOfTalent: {name: 'すべて表示', id: 0},
};

export const fetchCompanyChart = createAsyncThunk(
  "getCompanyChart",
  async (query: CompanyHomeTalentChartRetrieveData, thunkAPI: any) => {
    try {
      const response = await CompanyHomeService.companyHomeTalentChartRetrieve(query);
      if (response) {
        return response; 
      }
      return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI); 
    }
  }
);

export const fetchCompanyTalentHasChangedJob = createAsyncThunk(
  "getCompanyTalentHasChangedJob",
  async (query: CompanyHomeTalentHasChangedJobRetrieveData, thunkAPI: any) => {
    try {
      const response = await CompanyHomeService.companyHomeTalentHasChangedJobRetrieve(query);
      if (response) {
        return response; 
      }
      return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  }
)

export const fetchCompanyTopNewsAndJob = createAsyncThunk(
  "getCompanyTopNewsAndJob",
  async (query: CompanyHomeTopViewNewsAndJobRetrieveData, thunkAPI: any) => {
    try {
      const response = await CompanyHomeService.companyHomeTopViewNewsAndJobRetrieve(query);
      if (response) {
        return response; 
      }
      return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  }
);

export const getTypeOfTalent = createAsyncThunk(
  "getTypeOfTalent",
  async (data: MasterOptionsMasterListData, thunkAPI: any) => {
    try {
    const res: any = await MasterService.masterOptionsMasterList(data);
    if (res.success) {
      return res.data;
    } else {
      if(res.message) return thunkAPI.rejectWithValue(res.message);
      else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
    }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.TALENT, thunkAPI);
    }
  },
);

const companyHomeSlice = createSlice({
  name: 'companyHome',
  initialState,
  reducers: {
    setOpenDetailTalentPanel(state, action) {
      state.isOpenDetailTalentPanel = action.payload;
    },
    toggleRankAOnly(state) {
      state.rankAOnly = !state.rankAOnly;
    },
    selectChartSelectedTime(state, action: PayloadAction<SelectedTime>) {
      state.chartSelectedTime = action.payload;
    },
    setMemberInfo(state, action: PayloadAction<CompanyDashboardMemberData[]>) {
      state.memberRegisteredInfo = action.payload;
    },
    setSelectedTypeOfTalent(state, action: PayloadAction<any>) {
      state.selectedTypeOfTalent = action.payload;
    },
  },
  extraReducers: (builder) => {
    // reset state
    builder.addCase(resetAppState, () => initialState),
    
    //getCompanyChart
    builder.addCase(fetchCompanyChart.fulfilled, (state, action) => {
      state.memberRegisteredInfo = [...action.payload?.data];
      state.chartData = {...action.payload?.chart_data};
      state.isLoadingChart = false;
    }),
    builder.addCase(fetchCompanyChart.rejected, (state) => {
      state.isLoadingChart = false;
    }),
    builder.addCase(fetchCompanyChart.pending, (state) => {
      state.isLoadingChart = true;
    }),

    //getCompanyTalentHasChangedJob
    builder.addCase(fetchCompanyTalentHasChangedJob.fulfilled, (state, action) => {
      state.talentHasChangedJob = [...action.payload?.talentHasChangedJob];
      state.talentNewRegistrations = [...action.payload?.newRegistrations];
      state.isLoadingTalentHasChangedJob = false;
    }),
    builder.addCase(fetchCompanyTalentHasChangedJob.rejected, (state) => {
      state.isLoadingTalentHasChangedJob = false;
    }),
    builder.addCase(fetchCompanyTalentHasChangedJob.pending, (state) => {
      state.isLoadingTalentHasChangedJob = true;
    }),

    //getCompanyTopNewsAndJob
    builder.addCase(fetchCompanyTopNewsAndJob.fulfilled, (state, action) => {
      state.isLoadingTopNews =  false;
      state.topNews = [...action.payload?.topNews];
      state.topJobs = [...action.payload?.topJobs];
    }),
    builder.addCase(fetchCompanyTopNewsAndJob.rejected, (state) => {
      state.isLoadingTopNews = false;
    }),
    builder.addCase(fetchCompanyTopNewsAndJob.pending, (state) => {
      state.isLoadingTopNews = true;
    }),

    // getTypeOfTalent
    builder.addCase(getTypeOfTalent.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    builder.addCase(getTypeOfTalent.fulfilled, (state, action) => {
      state.loading = false;
      const payload = Array.isArray(action.payload) ? action.payload : [];

      const talentTypeOptions = payload
        .filter(
          (option: any) =>
            option?.name === TALENT_TYPE_OPTION_TYPE &&
            Array.isArray(option?.option_list)
        )
        .flatMap((option: any) =>
          option.option_list.map((subOption: any) => ({
            name: subOption?.label ?? '',
            id: subOption?.id ?? 0,
            value: subOption?.value ?? '',
          }))
        );

      state.typeOfTalent = [
        { name: 'すべて表示', id: 0 },
        ...talentTypeOptions,
      ];
    });
    builder.addCase(getTypeOfTalent.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    })
  },
});

export const { 
  toggleRankAOnly, 
  setOpenDetailTalentPanel,
  selectChartSelectedTime, 
  setMemberInfo,
  setSelectedTypeOfTalent,
} = companyHomeSlice.actions;
export default companyHomeSlice.reducer;